import { ChevronDownIcon, cn, Sheet } from "@au-schein-de/au-ui-library";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	PHARMACY_PICKER_FILTER_SHEET_CONTENT_CLASS,
	PHARMACY_PICKER_FILTER_SHEET_Z_CLASS,
	PharmacyPickerFilterSheetHeader,
} from "~/features/products/pharmacy-picker/pharmacy-picker-filter-sheet-header.component";
import {
	ANY_RADIUS,
	buildRadiusOptions,
	type PharmacyWithDistance,
	type RadiusOptionValue,
} from "./radius-options";

interface RadiusSelectSheetProps {
	value: RadiusOptionValue;
	pharmacyMarkers: readonly PharmacyWithDistance[];
	onChange: (value: RadiusOptionValue) => void;
	disabled?: boolean;
	triggerClassName?: string;
}

export function RadiusSelectSheet({
	value,
	pharmacyMarkers,
	onChange,
	disabled = false,
	triggerClassName,
}: Readonly<RadiusSelectSheetProps>) {
	const { t } = useTranslation(["product", "common"]);
	const [isOpen, setIsOpen] = useState(false);
	const [draft, setDraft] = useState<RadiusOptionValue>(value);
	const options = useMemo(
		() => buildRadiusOptions(pharmacyMarkers),
		[pharmacyMarkers],
	);
	const title = t("vendor.map.radius.title");
	const triggerLabel =
		value === ANY_RADIUS
			? t("vendor.map.radius.anyCompact")
			: t("vendor.map.radius.kmCompact", { km: value });
	const formatOptionLabel = (option: RadiusOptionValue): string =>
		option === ANY_RADIUS
			? t("vendor.map.radius.any")
			: t("vendor.map.radius.km", { km: option });
	const openSheet = (): void => {
		if (disabled) return;
		setDraft(value);
		setIsOpen(true);
	};
	const applyDraft = (): void => {
		onChange(draft);
		setIsOpen(false);
	};
	return (
		<>
			<button
				type="button"
				disabled={disabled}
				aria-label={title}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				data-testid="pharmacy-radius-select"
				data-state={isOpen ? "open" : "closed"}
				className={cn(
					"flex h-9 shrink-0 items-center gap-2 rounded-xl border-2 bg-gray-200 p-3 text-body-sm font-medium text-gray-700",
					isOpen ? "border-ansay-700" : "border-transparent",
					"disabled:cursor-not-allowed disabled:opacity-50",
					triggerClassName,
				)}
				onClick={openSheet}
			>
				<span className="text-body-sm-emphasis">{triggerLabel}</span>
				<span className={cn("flex", isOpen && "rotate-180")}>
					<ChevronDownIcon size={5} />
				</span>
			</button>
			<Sheet.Root
				open={isOpen}
				onOpenChange={(nextOpen) => {
					if (nextOpen) {
						setDraft(value);
					}
					setIsOpen(nextOpen);
				}}
			>
				<Sheet.Portal>
					<Sheet.Overlay className={PHARMACY_PICKER_FILTER_SHEET_Z_CLASS} />
					<Sheet.Content
						side="bottom"
						className={PHARMACY_PICKER_FILTER_SHEET_CONTENT_CLASS}
						data-testid="pharmacy-radius-sheet"
						aria-describedby={undefined}
					>
						<PharmacyPickerFilterSheetHeader
							title={title}
							onClose={() => setIsOpen(false)}
							closeLabel={t("close", { ns: "common" })}
							closeTestId="pharmacy-radius-sheet-close"
						/>
						<div className="flex flex-col gap-2">
							{options.map(({ value: option, count }) => {
								const isSelected = option === draft;
								const isEmpty = count === 0;
								return (
									<button
										key={String(option)}
										type="button"
										disabled={isEmpty}
										onClick={() => setDraft(option)}
										className={cn(
											"flex h-12 w-full items-center justify-between rounded-xl bg-white px-3 text-left text-body-bold text-gray-700",
											isSelected
												? "border-2 border-ansay-500"
												: "border-2 border-transparent",
											isEmpty && "cursor-not-allowed bg-gray-100 text-gray-400",
										)}
										data-testid={`pharmacy-radius-option-${option}`}
									>
										<span>{formatOptionLabel(option)}</span>
										<span
											className={cn(
												"text-body",
												isEmpty ? "text-gray-400" : "text-gray-500",
											)}
										>
											{count}
										</span>
									</button>
								);
							})}
						</div>
						<button
							type="button"
							className="mt-6 w-full rounded-xl bg-ansay-light p-4 text-body-emphasis text-gray-700 transition-opacity hover:opacity-90"
							onClick={applyDraft}
							data-testid="pharmacy-radius-apply"
						>
							{t("vendor.map.radius.apply")}
						</button>
					</Sheet.Content>
				</Sheet.Portal>
			</Sheet.Root>
		</>
	);
}
